import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, CardActions, Button, Typography, Grid, Alert, CircularProgress } from '@mui/material';
import {
  Groups,
  HowToReg,
  EventBusy,
  BeachAccess,
  PendingActions,
  WorkOutline,
  Cake,
  PersonAdd,
  TrendingDown,
  AccessTime,
  Payments,
} from '@mui/icons-material';
import { PageHeader, StatCard } from '../../components/common/pageKit';
import { hrService, HrSummary } from '../../services/hrService';
import EmployeesPage from './EmployeesPage';
import AttendancePage from './AttendancePage';
import LeavePage from './LeavePage';
import PayrollPage from './PayrollPage';
import RecruitmentPage from './RecruitmentPage';
import HrCommandCenter from './HrCommandCenter';
import HrAnalyticsPage from './HrAnalyticsPage';
import HrMyDayPage from './HrMyDayPage';
import Employee360Page from './Employee360Page';
import EmployeeFormPage from './EmployeeFormPage';
import RecruitmentAtsPage from './RecruitmentAtsPage';
import OrgChartPage from './OrgChartPage';
import PerformancePage from './PerformancePage';
import LearningPage from './LearningPage';
import LifecyclePage from './LifecyclePage';
import SalaryStructuresPage from './SalaryStructuresPage';
import ShiftsAttendancePlusPage from './ShiftsAttendancePlusPage';
import TimePayrollCommandCenter from './TimePayrollCommandCenter';
import AttendanceControlCenter from './AttendanceControlCenter';
import LeaveControlCenter from './LeaveControlCenter';
import PayrollRunWorkspace from './PayrollRunWorkspace';

interface HrModuleCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  color: string;
  /** Real figure from /hr/summary for this card — omitted entirely when there is none. */
  count?: number | null;
  countLabel?: string;
}

const HrOverview: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<HrSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    hrService
      .getSummary()
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.message || 'Failed to load HR summary');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const modules: HrModuleCard[] = [
    {
      title: 'Employees',
      description: 'Directory, department & designation assignment, employment status',
      icon: <Groups />,
      path: '/hr/employees',
      color: '#0284c7',
      count: summary?.headcount ?? null,
      countLabel: 'active headcount',
    },
    {
      title: 'Attendance',
      description: "Today's presence, check-in / check-out, date-range history",
      icon: <AccessTime />,
      path: '/hr/attendance',
      color: '#059669',
      count: summary?.presentToday ?? null,
      countLabel: 'present today',
    },
    {
      title: 'Leave',
      description: 'Requests, approvals and per-employee leave balances',
      icon: <BeachAccess />,
      path: '/hr/leave',
      color: '#d97706',
      count: summary?.pendingLeaveRequests ?? null,
      countLabel: 'pending requests',
    },
    {
      title: 'Payroll',
      description: 'Monthly runs, payslip generation and approval lifecycle',
      icon: <Payments />,
      path: '/hr/payroll',
      color: '#7c3aed',
    },
    {
      title: 'Recruitment',
      description: 'Job openings and applications by pipeline stage',
      icon: <WorkOutline />,
      path: '/hr/recruitment',
      color: '#dc2626',
      count: summary?.openPositions ?? null,
      countLabel: 'open positions',
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Human Resources"
        subtitle="Departments, attendance, leave, payroll and recruitment"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard title="Headcount" value={summary?.headcount ?? null} icon={<Groups />} color="#0284c7" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard title="Present Today" value={summary?.presentToday ?? null} icon={<HowToReg />} color="#059669" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard title="Absent Today" value={summary?.absentToday ?? null} icon={<EventBusy />} color="#dc2626" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard title="On Leave Today" value={summary?.onLeaveToday ?? null} icon={<BeachAccess />} color="#0891b2" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Pending Leave Requests"
                value={summary?.pendingLeaveRequests ?? null}
                icon={<PendingActions />}
                color="#d97706"
                onClick={() => navigate('/hr/leave')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Open Positions"
                value={summary?.openPositions ?? null}
                icon={<WorkOutline />}
                color="#7c3aed"
                onClick={() => navigate('/hr/recruitment')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard title="Birthdays This Week" value={summary?.birthdaysThisWeek ?? null} icon={<Cake />} color="#db2777" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard title="Joining This Month" value={summary?.joiningThisMonth ?? null} icon={<PersonAdd />} color="#0d9488" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Attrition Rate (12mo)"
                value={summary?.attritionRate === null || summary?.attritionRate === undefined ? null : `${summary.attritionRate}%`}
                icon={<TrendingDown />}
                color="#e11d48"
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Modules
          </Typography>
          <Grid container spacing={2}>
            {modules.map((m) => (
              <Grid key={m.title} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card
                  elevation={0}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          backgroundColor: m.color,
                          color: '#fff',
                          flexShrink: 0,
                          '& svg': { fontSize: 20 },
                        }}
                      >
                        {m.icon}
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {m.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: m.count != null ? 1 : 0 }}>
                      {m.description}
                    </Typography>
                    {m.count != null && (
                      <Typography variant="caption" sx={{ fontWeight: 700, color: m.color, fontVariantNumeric: 'tabular-nums' }}>
                        {m.count} {m.countLabel}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    <Button size="small" onClick={() => navigate(m.path)} sx={{ fontWeight: 700 }}>
                      Open
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

const HrPage: React.FC = () => {
  return (
    <Routes>
      <Route index element={<HrCommandCenter />} />
      <Route path="overview" element={<HrOverview />} />
      <Route path="analytics" element={<HrAnalyticsPage />} />
      <Route path="my-day" element={<HrMyDayPage />} />
      <Route path="employees" element={<EmployeesPage />} />
      <Route path="employees/new" element={<EmployeeFormPage />} />
      <Route path="employees/:id/edit" element={<EmployeeFormPage />} />
      <Route path="employees/:id/360" element={<Employee360Page />} />
      <Route path="org-chart" element={<OrgChartPage />} />
      <Route path="time-payroll" element={<TimePayrollCommandCenter />} />
      <Route path="attendance" element={<AttendancePage />} />
      <Route path="attendance-control" element={<AttendanceControlCenter />} />
      <Route path="shifts" element={<ShiftsAttendancePlusPage />} />
      <Route path="leave" element={<LeavePage />} />
      <Route path="leave-control" element={<LeaveControlCenter />} />
      <Route path="payroll" element={<PayrollPage />} />
      <Route path="payroll/:id" element={<PayrollRunWorkspace />} />
      <Route path="salary-structures" element={<SalaryStructuresPage />} />
      <Route path="performance" element={<PerformancePage />} />
      <Route path="learning" element={<LearningPage />} />
      <Route path="lifecycle" element={<LifecyclePage />} />
      <Route path="recruitment" element={<RecruitmentPage />} />
      <Route path="recruitment-ats" element={<RecruitmentAtsPage />} />
    </Routes>
  );
};

export default HrPage;
